import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _dec3, _dec4, _dec5, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8;
import uuid from 'uuid';
import { action, observable } from 'mobx';
import api from 'core/util/api';
import { Client } from 'core/util/sseConnect';
import { sortResultRows, DEFAULT_VIZ_LIMIT } from '@kentik/ui-shared/query/query';
import UdeQueryResultsCollection from './UdeQueryResultsCollection';
import RawEventCollection from './RawEventCollection';
let QueryRequest = (_dec = observable.ref, _dec2 = observable.ref, _dec3 = observable.ref, _dec4 = observable.ref, _dec5 = observable.ref, _class = class QueryRequest {
  get settings() {
    return this.queryModel.settings;
  }
  constructor(options) {
    this.type = void 0;
    this.queryModel = void 0;
    _initializerDefineProperty(this, "status", _descriptor, this);
    this.requestId = '';
    _initializerDefineProperty(this, "queryStartTime", _descriptor2, this);
    _initializerDefineProperty(this, "queryDurationMs", _descriptor3, this);
    _initializerDefineProperty(this, "iprDebug", _descriptor4, this);
    _initializerDefineProperty(this, "timings", _descriptor5, this);
    this.query = void 0;
    this.results = new UdeQueryResultsCollection({
      timestamps: [],
      rows: []
    }, {
      parent: this
    });
    _initializerDefineProperty(this, "rawRows", _descriptor6, this);
    _initializerDefineProperty(this, "rawColumnKeys", _descriptor7, this);
    this.rawEventsCollection = new RawEventCollection();
    this.sseClient = void 0;
    this.applicationMetadata = void 0;
    /** Row IDs that have a corresponding chart series/point. Set by the chart data view. */
    _initializerDefineProperty(this, "chartedRowIds", _descriptor8, this);
    this.type = options.type ?? 'main';
    this.queryModel = options.queryModel;
    this.query = this.queryModel.query;
    const {
      results
    } = options;
    if (results) {
      this.results.replace(results);
      this.rawRows = results.rawRows ?? [];
      this.rawColumnKeys = results.rawColumnKeys ?? [];
      this.rawEventsCollection.replace(this.rawRows);
      this.status = 'done';
    }
    this.sseClient = new Client({
      outType: 'ude/query',
      inType: 'ude/result',
      onSuccess: _data => {
        const data = _data;
        const responseId = data?.requestID;
        const parentId = data?.parentRequestID;
        if (responseId && responseId !== this.requestId && parentId !== this.requestId) {
          return;
        }
        if (responseId === this.requestId && !this.isLiveUpdate(data)) {
          const debug = data.iprDebug;
          this.iprDebug = debug?.requestID === this.requestId ? debug : null;
          this.timings = data.timings ?? null;
        }
        if (this.isLiveUpdate(data)) {
          this.applyLiveUpdate(data);
        } else {
          this.results.replace(data);
          this.rawRows = data.rawRows ?? [];
          this.rawColumnKeys = data.rawColumnKeys ?? [];
          this.rawEventsCollection.replace(this.rawRows);
        }
        if (this.queryStartTime !== null && this.queryDurationMs === null) {
          this.queryDurationMs = Date.now() - this.queryStartTime;
        }
        this.status = 'done';
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: data => {
        if (this.requestId === data.requestID) {
          this.iprDebug = data.iprDebug?.requestID === this.requestId ? data.iprDebug : null;
          this.timings = data.timings ?? null;
          this.status = 'error';
          if (this.queryStartTime !== null && this.queryDurationMs === null) {
            this.queryDurationMs = Date.now() - this.queryStartTime;
          }
        }
      },
      onReconnect: () => {
        if (this.status !== 'init') {
          this.results.reset();
          // QueryModel re-runs the main and mirror requests together
          if (this.type === 'main') {
            this.queryModel.requestRun();
          }
        }
      }
    });
  }
  isLiveUpdate(data) {
    return typeof data === 'object' && data !== null && 'liveUpdate' in data && data.liveUpdate === true;
  }

  /**
   * Merge incremental live update data into the existing results and trim
   * timestamps that fall outside the sliding window.
   */
  applyLiveUpdate(update) {
    if (!this.query) {
      return;
    }
    const {
      timestamps: newTimestamps,
      rows: newRows,
      windowSize = 0
    } = update;
    const prev = this.results.serialize();
    const tsIndexMap = new Map();
    for (let i = 0; i < prev.timestamps.length; i++) {
      tsIndexMap.set(prev.timestamps[i], {
        source: 'old',
        idx: i
      });
    }
    for (let i = 0; i < newTimestamps.length; i++) {
      tsIndexMap.set(newTimestamps[i], {
        source: 'new',
        idx: i
      });
    }
    const allTs = Array.from(tsIndexMap.keys()).sort((a, b) => a - b);
    const latestTimestamp = allTs[allTs.length - 1] ?? 0;
    const cutoff = latestTimestamp - windowSize;
    const trimmedTimestamps = allTs.filter(ts => ts >= cutoff);
    const sources = trimmedTimestamps.map(ts => tsIndexMap.get(ts));

    // build a map of existing rows by id for fast lookup
    const rowMap = new Map(prev.rows.map(row => [row.id, row]));

    // merge each incoming row
    for (const newRow of newRows) {
      const existing = rowMap.get(newRow.id);
      if (existing) {
        const mergedValues = {};
        for (const metric of Object.keys(existing.values)) {
          const existingVals = existing.values[metric].values;
          const incomingVals = newRow.values[metric]?.values ?? [];
          const values = sources.map(s => s.source === 'old' ? existingVals[s.idx] ?? null : incomingVals[s.idx] ?? null);
          mergedValues[metric] = {
            values
          };
        }
        for (const metric of Object.keys(newRow.values)) {
          if (!mergedValues[metric]) {
            const incomingVals = newRow.values[metric].values;
            const values = sources.map(s => s.source === 'new' ? incomingVals[s.idx] ?? null : null);
            mergedValues[metric] = {
              values
            };
          }
        }
        rowMap.set(newRow.id, {
          ...existing,
          values: mergedValues,
          rollups: newRow.rollups,
          dimensions: newRow.dimensions
        });
      } else {
        const paddedValues = {};
        for (const metric of Object.keys(newRow.values)) {
          const incomingVals = newRow.values[metric].values;
          const values = sources.map(s => s.source === 'new' ? incomingVals[s.idx] ?? null : null);
          paddedValues[metric] = {
            values
          };
        }
        rowMap.set(newRow.id, {
          ...newRow,
          values: paddedValues
        });
      }
    }
    // If user has an active selection (any entry in the visibility map),
    // default new rows to hidden so they don't break a solo/selection.
    if (this.queryModel.seriesVisibility.size > 0) {
      for (const newRow of newRows) {
        const isNewRow = !prev.rows.some(r => r.id === newRow.id);
        if (isNewRow && !this.queryModel.seriesVisibility.has(newRow.id)) {
          this.queryModel.seriesVisibility.set(newRow.id, false);
        }
      }
    }

    // Live updates accumulate rows across batches (rowMap is seeded from the previous
    // results and merged with each incoming batch), so the merged set can grow past
    // viz.limit even though the server caps each individual response. Re-apply the cap
    // here, selecting which series survive using the same ordering the server applies,
    // so we strip the least-relevant series rather than the most recently added ones.
    const allRows = Array.from(rowMap.values());
    const limit = this.query.viz?.limit ?? DEFAULT_VIZ_LIMIT;
    const keepIds = new Set(sortResultRows(this.query, allRows).slice(0, limit).map(row => row.id));
    this.results.update({
      timestamps: trimmedTimestamps,
      rows: allRows.map(row => keepIds.has(row.id) ? row : {
        ...row,
        values: {}
      })
    });
  }
  get hasRun() {
    return this.status !== 'init';
  }
  get isLoading() {
    return this.status === 'running' || this.status === 'partial' && this.results.rows.length === 0;
  }
  get hasError() {
    return this.status === 'error';
  }
  get hasResults() {
    return (this.status === 'partial' || this.status === 'done') && this.results.rows.length > 0;
  }
  get fullyLoaded() {
    return this.status === 'done';
  }

  /** Check if a row has a corresponding chart series/point.
   *  When chartedRowIds is empty (chart hasn't reported yet), treat all rows as charted
   *  so the legend doesn't flash in a grayed-out state on initial load.
   */
  isRowCharted(rowId) {
    return this.chartedRowIds.size === 0 || this.chartedRowIds.has(rowId);
  }
  async run(query, application_metadata) {
    const previousRequestId = this.requestId;
    const requestID = uuid.v4();
    this.query = query;
    this.applicationMetadata = application_metadata;
    this.status = 'running';
    this.requestId = requestID;
    this.queryStartTime = Date.now();
    this.queryDurationMs = null;
    this.iprDebug = null;
    this.timings = null;
    try {
      if (previousRequestId && previousRequestId !== requestID) {
        this.sseClient.send({
          action: 'RUN_UNSUBSCRIBE',
          requestID: previousRequestId
        });
      }
      this.sseClient.send({
        action: 'RUN_QUERY',
        query,
        requestID,
        application_metadata
      });
    } catch (_e) {
      if (this.requestId === requestID) {
        this.status = 'error';
      }
    }
  }
  reset() {
    if (this.requestId) {
      this.sseClient.send({
        action: 'RUN_UNSUBSCRIBE',
        requestID: this.requestId
      });
    }
    this.status = 'init';
    this.requestId = '';
    this.applicationMetadata = undefined;
    this.results.reset();
    this.rawRows = [];
    this.rawColumnKeys = [];
    this.rawEventsCollection.reset();
  }

  /** Stops the server subscription (e.g. live update); last results stay until the next run. */
  unsubscribe() {
    if (this.requestId) {
      this.sseClient.send({
        action: 'RUN_UNSUBSCRIBE',
        requestID: this.requestId
      });
      this.requestId = '';
    }
    // no result will arrive anymore; also releases the board load slot
    if (this.status === 'running') {
      this.status = 'done';
    }
  }
  cleanup() {
    this.sseClient.send({
      action: 'RUN_UNSUBSCRIBE',
      requestID: this.requestId
    });
    this.sseClient.cleanup();
  }
  fetchSql() {
    return api.post('/api/ui/ude-debug/sql', {
      data: {
        query: this.query
      }
    }).then(res => ({
      ...res,
      type: this.type
    }));
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "status", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 'init';
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "queryStartTime", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "queryDurationMs", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "iprDebug", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "timings", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "rawRows", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "rawColumnKeys", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "chartedRowIds", [_dec5], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Set();
  }
}), _applyDecoratedDescriptor(_class.prototype, "applyLiveUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "applyLiveUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "run", [action], Object.getOwnPropertyDescriptor(_class.prototype, "run"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unsubscribe", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unsubscribe"), _class.prototype), _class);
export { QueryRequest as default };